// Mobile browser debugging utilities

export const isMobileBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const getBrowserInfo = () => {
  if (typeof window === 'undefined') return null;
  
  const userAgent = navigator.userAgent;
  const isMobile = isMobileBrowser();
  
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';
  
  if (userAgent.includes('Chrome')) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.includes('Edge')) {
    browserName = 'Edge';
    const match = userAgent.match(/Edge\/(\d+)/);
    if (match) browserVersion = match[1];
  }
  
  return {
    userAgent,
    isMobile,
    browserName,
    browserVersion,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    connection: (navigator as any).connection ? {
      effectiveType: (navigator as any).connection.effectiveType,
      downlink: (navigator as any).connection.downlink,
      rtt: (navigator as any).connection.rtt
    } : null
  };
};

export const logMobileDebugInfo = (context: string, additionalData?: any) => {
  const browserInfo = getBrowserInfo();
  
  console.group(`📱 Mobile Debug - ${context}`);
  console.log('Browser Info:', browserInfo);
  console.log('Timestamp:', new Date().toISOString());
  console.log('Additional Data:', additionalData);
  console.groupEnd();
};

export const createMobileErrorReport = (error: Error, context: string) => {
  const browserInfo = getBrowserInfo();
  
  return {
    error: {
      message: error.message,
      stack: error.stack,
      name: error.name
    },
    context,
    browserInfo,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent
  };
};

export const retryWithMobileOptimization = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000,
  operationName: string = 'Operation'
): Promise<T> => {
  const isMobile = isMobileBrowser();
  const delay = isMobile ? baseDelay * 1.5 : baseDelay; // Longer delays for mobile
  const retries = isMobile ? Math.max(maxRetries, 4) : maxRetries; // More retries for mobile
  
  let lastError: Error | undefined;
  
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`🔄 ${operationName} - Attempt ${attempt}/${retries} (Mobile: ${isMobile})`);
      const result = await operation();
      console.log(`✅ ${operationName} - Success on attempt ${attempt}`);
      return result;
    } catch (error) {
      lastError = error as Error;
      console.warn(`❌ ${operationName} - Attempt ${attempt} failed:`, error);
      
      if (attempt < retries) {
        const waitTime = delay * attempt * (isMobile ? 1.2 : 1); // Exponential backoff with mobile multiplier
        console.log(`⏳ ${operationName} - Waiting ${waitTime}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }
  
  console.error(`💥 ${operationName} - All ${retries} attempts failed`);
  throw lastError || new Error(`${operationName} failed after ${retries} attempts`);
};

export const checkMobileCompatibility = () => {
  const browserInfo = getBrowserInfo();
  const issues: string[] = [];
  
  if (browserInfo?.isMobile) {
    // Check for known problematic mobile browsers
    if (browserInfo.userAgent.includes('Opera Mini')) {
      issues.push('Opera Mini has limited JavaScript support');
    }
    
    if (browserInfo.userAgent.includes('UCBrowser')) {
      issues.push('UC Browser may have compatibility issues');
    }
    
    // Check for very old mobile browsers
    if (browserInfo.userAgent.includes('Android 4.') || browserInfo.userAgent.includes('iPhone OS 9_')) {
      issues.push('Very old mobile browser detected - may have limited support');
    }
    
    // Check connection quality
    if (browserInfo.connection && browserInfo.connection.effectiveType === 'slow-2g') {
      issues.push('Very slow connection detected - may cause timeouts');
    }
  }
  
  return {
    isCompatible: issues.length === 0,
    issues,
    browserInfo
  };
};

